import { useEffect, useRef, useCallback } from 'react'

/**
 * Custom hook for handling modal persistence on visibility changes and unmount
 * Saves data when modal is closed, tab becomes inactive, or component unmounts
 */
export const useModalPersistence = (saveCallback, isOpen) => {
  const saveCallbackRef = useRef(saveCallback)
  const isOpenRef = useRef(isOpen)
  const hasUnsavedChangesRef = useRef(false)

  // Update refs when props change
  useEffect(() => {
    saveCallbackRef.current = saveCallback
    isOpenRef.current = isOpen
  }, [saveCallback, isOpen])

  /**
   * Handle visibility change (tab switching)
   */
  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState === 'hidden' && isOpenRef.current) {
      console.log('Modal persistence: Tab became hidden, saving assignments...')
      if (saveCallbackRef.current) {
        saveCallbackRef.current()
      }
    }
  }, [])

  /**
   * Handle page unload
   */
  const handleBeforeUnload = useCallback(() => {
    if (isOpenRef.current) {
      console.log('Modal persistence: Page unloading, saving assignments...')
      if (saveCallbackRef.current) {
        saveCallbackRef.current()
      }
    }
  }, [])

  /**
   * Handle modal close
   */
  const handleModalClose = useCallback(() => {
    console.log('Modal persistence: Modal closing, saving assignments...')
    if (saveCallbackRef.current) {
      saveCallbackRef.current()
    }
  }, [])

  /**
   * Set up event listeners
   */
  useEffect(() => {
    // Only set up listeners if modal is open
    if (!isOpen) return

    // Add visibility change listener
    document.addEventListener('visibilitychange', handleVisibilityChange)
    
    // Add beforeunload listener
    window.addEventListener('beforeunload', handleBeforeUnload)

    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [isOpen, handleVisibilityChange, handleBeforeUnload])

  /**
   * Save on unmount if modal is still open
   */
  useEffect(() => {
    return () => {
      if (isOpenRef.current) {
        console.log('Modal persistence: Component unmounting, saving assignments...')
        if (saveCallbackRef.current) {
          saveCallbackRef.current()
        }
      }
    }
  }, [])

  /**
   * Mark that there are unsaved changes
   */
  const markUnsavedChanges = useCallback(() => {
    hasUnsavedChangesRef.current = true
  }, [])

  /**
   * Mark that changes have been saved
   */
  const markChangesSaved = useCallback(() => {
    hasUnsavedChangesRef.current = false
  }, [])

  /**
   * Enhanced close handler that saves before closing
   */
  const createCloseHandler = useCallback((originalCloseHandler) => {
    return (...args) => {
      handleModalClose()
      if (originalCloseHandler) {
        originalCloseHandler(...args)
      }
    }
  }, [handleModalClose])

  return {
    handleModalClose,
    markUnsavedChanges,
    markChangesSaved,
    createCloseHandler,
    hasUnsavedChanges: hasUnsavedChangesRef.current
  }
}